import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoSaveResult } from '@nkr/models/opo-document';
import { IOpoTaxesData, IOpoTaxRequest } from '@nkr/models/opo-tax';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

interface IRetVal {
    data: IOpoTaxesData | null,
    loading: boolean,
    error: string,
    save: (taxId: number | null, request: IOpoTaxRequest) => Promise<IOpoSaveResult>,
    remove: (taxId: number) => Promise<IOpoSaveResult>
}

const useOpoTaxes = (): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [data, setData] = useState<IOpoTaxesData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const baseUrl = `${__API__}/opo/${opoId}/taxes`;

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync('Getting tax rates, one moment please...'));

            try {
                const response: ITypedBaseResponse<IOpoTaxesData> = await dispatch(AuthActionCreators.apiGet({ url: baseUrl }));

                if (!isCurrent) {
                    return;
                }

                if (response && response.data.success) {
                    setData(response.data.results);
                } else {
                    setError('Unable to load tax rates');
                }
            } catch (err) {
                if (isCurrent) {
                    setError('Unable to load tax rates');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId, refreshKey]);

    const runAction = async (message: string, action: () => Promise<ITypedBaseResponse<any>>, failureMessage: string): Promise<IOpoSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync(message));

        try {
            const response = await action();

            if (response && response.data.success) {
                setRefreshKey(x => x + 1);
                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || failureMessage };
        } catch (err) {
            return { success: false, message: failureMessage };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const save = (taxId: number | null, request: IOpoTaxRequest) => runAction(
        'Saving tax rate, one moment please...',
        () => dispatch(taxId
            ? AuthActionCreators.apiPut({ url: `${baseUrl}/${taxId}`, data: request })
            : AuthActionCreators.apiPost({ url: baseUrl, data: request })),
        'Unable to save the tax rate');

    const remove = (taxId: number) => runAction(
        'Deleting tax rate, one moment please...',
        () => dispatch(AuthActionCreators.apiDelete({ url: `${baseUrl}/${taxId}` })),
        'Unable to delete the tax rate');

    return { data, loading, error, save, remove };
}

export default useOpoTaxes;
